import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CheckRow, CrmActivity, CrmAuth, CrmLists, CrmPerson, CrmRules, LeadRow as RowIn, TeamRow } from './crmApi';
import { HolidayBanner } from './Holidays';
import LeadPanel, { JunkLeadModal } from './LeadPanel';
import { Empty, Head, LeadRow, PageHead, ShiftChip, Spinner, WAIT_LABEL, btnGold, fmtHm, fmtPhone, fmtWhen, forgetCrmMe, goldBg, inputCls, labelCls, purpleBg, useCrmMe } from './ui';

// ── The Lead Manager's CRM pages (Sales Admin and the admin get them too) ──

const STAGES = ['New', 'Not Picked', 'Call Later', 'Poll Sent', 'Interested', 'No Response', 'Future Prospect', 'Direct Visit', 'Converted', 'Cold Lead', 'Not Interested', 'Junk Lead'];

// ── ADD LEADS ────────────────────────────────────────────────

// Which spreadsheet column is which, from its heading
const COLUMN_WORDS: Record<keyof RowIn, RegExp> = {
  name:   /name/i,
  phone:  /phone|mobile|number|contact|whatsapp|cell/i,
  email:  /e-?mail/i,
  course: /course|program/i,
  source: /source|campaign|platform/i,
  notes:  /note|remark|comment/i,
  leadType: /lead.?type|^type$|channel/i,
  mode:   /online|offline|^mode$|class.?mode/i,
};
const FIELD_LABEL: Record<keyof RowIn, string> = { name: 'Name', phone: 'Phone', email: 'Email', course: 'Course', source: 'Source', notes: 'Notes', leadType: 'Lead type', mode: 'Online / offline' };

// WhatsApp lead or form lead: two big buttons
export const TypePick: React.FC<{ value: string; onPick: (v: string) => void; label: string }> = ({ value, onPick, label }) => (
  <div>
    <p className={labelCls}>{label}</p>
    <div className="flex gap-2" role="radiogroup" aria-label={label}>
      {[['WhatsApp', 'WhatsApp lead', 'Messaged us on WhatsApp'], ['Form', 'Form lead', 'Filled a form']].map(([k, title, hint]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onPick(k)}
          className="flex-1 px-3 py-2 rounded-xl border-2 text-left transition-all"
          style={value === k ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' } : { background: '#fff', color: '#374151', borderColor: '#e5e7eb' }}>
          <span className="block text-sm font-bold">{title}</span>
          <span className="block text-[10px] opacity-70">{hint}</span>
        </button>
      ))}
    </div>
  </div>
);
const FIELDS = Object.keys(COLUMN_WORDS) as (keyof RowIn)[];

function guessColumns(header: string[]): Record<keyof RowIn, number> {
  const map = {} as Record<keyof RowIn, number>;
  for (const f of FIELDS) map[f] = header.findIndex(h => COLUMN_WORDS[f].test(String(h || '')));
  // "Phone number" also says "name"-ish words sometimes: keep them apart
  if (map.name === map.phone) map.name = header.findIndex((h, i) => i !== map.phone && /name/i.test(String(h || '')));
  return map;
}

const CHECK_LABEL: Record<CheckRow['check'], { text: string; cls: string }> = {
  new:       { text: 'New', cls: 'text-green-700' },
  existing:  { text: 'Already a lead', cls: 'text-purple-700' },
  student:   { text: 'Current student', cls: 'text-gray-500' },
  invalid:   { text: 'Wrong number', cls: 'text-red-600' },
  duplicate: { text: 'Twice in this file', cls: 'text-gray-500' },
};

// Pasted numbers: one per comma, semicolon or line. A piece with several
// numbers and only spaces between them ("98765 43210 91234 56789") is split
// too; digits left over show up as a wrong number.
const MOBILE = /(?:\+?91[\s-]*|0)?[6-9](?:[\s-]*\d){9}/g;
function splitNumbers(text: string): string[] {
  const out: string[] = [];
  for (const piece of text.split(/[,;\n\r\t]+/).map(s => s.trim()).filter(Boolean)) {
    if (piece.replace(/\D/g, '').length <= 13) { out.push(piece); continue; }
    const found: string[] = piece.match(MOBILE) || [];
    out.push(...found.map(s => s.trim()));
    const rest = found.reduce((s: string, m: string) => s.replace(m, ' '), piece).replace(/\D/g, '');
    if (rest) out.push(rest);
  }
  return out;
}

const CheckChips: React.FC<{ counts: Record<string, number>; what: string }> = ({ counts, what }) => (
  <div className="flex flex-wrap gap-2 text-xs font-bold">
    <span className="px-3 py-1 rounded-full bg-green-100 text-green-800">{counts.new} new</span>
    {counts.existing > 0 && <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-800">{counts.existing} already leads: they keep their salesperson</span>}
    {counts.student > 0 && <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">{counts.student} current students: skipped</span>}
    {counts.invalid > 0 && <span className="px-3 py-1 rounded-full bg-red-100 text-red-700">{counts.invalid} wrong numbers: skipped</span>}
    {counts.duplicate > 0 && <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">{counts.duplicate} twice in the {what}</span>}
  </div>
);

const GiveTo: React.FC<{ people: CrmPerson[]; value: string; onChange: (v: string) => void; id: string }> = ({ people, value, onChange, id }) => (
  <select id={id} value={value} onChange={e => onChange(e.target.value)} className={inputCls}>
    <option value="turn">Hand out in turn</option>
    {people.map(p => <option key={p.id} value={p.id}>Give to {p.name}{p.role !== 'Salesperson' ? ` (${p.role})` : ''}</option>)}
  </select>
);

export const CrmAddLeads: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const { me, error } = useCrmMe(auth);
  const [one, setOne] = useState({ name: '', phone: '', email: '', course: '', source: '', notes: '', leadType: '', mode: '' });
  const [oneTo, setOneTo] = useState('turn');
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState<{ name: string; header: string[]; rows: string[][] } | null>(null);
  const [cols, setCols] = useState<Record<keyof RowIn, number> | null>(null);
  const [check, setCheck] = useState<{ rows: CheckRow[]; counts: Record<string, number> } | null>(null);
  const [upSource, setUpSource] = useState('');
  const [upTo, setUpTo] = useState('turn');
  const [upType, setUpType] = useState('');
  // Paste numbers
  const [pasted, setPasted] = useState('');
  const [pasteTo, setPasteTo] = useState('turn');
  const [pasteType, setPasteType] = useState('');
  const [pasteSource, setPasteSource] = useState('');
  const [pasteCheck, setPasteCheck] = useState<{ rows: CheckRow[]; counts: Record<string, number> } | null>(null);
  const numbers = useMemo(() => splitNumbers(pasted), [pasted]);

  const people = useMemo(() => (me?.people || []).filter(p => p.takesLeads), [me]);

  const checkPasted = async () => {
    if (!numbers.length) { toast.error('Paste the phone numbers first'); return; }
    setSaving(true);
    try { setPasteCheck(await crm.checkLeads(auth, numbers.map(phone => ({ phone })))); } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const savePasted = async () => {
    if (!pasteType) { toast.error('Pick WhatsApp leads or Form leads'); return; }
    setSaving(true);
    try {
      const r = await crm.importLeads(auth, numbers.map(phone => ({ phone })), pasteTo, pasteSource, pasteType);
      const c = r.counts;
      const to = pasteTo === 'turn' ? '' : people.find(p => p.id === pasteTo)?.name || '';
      toast.success(`Saved ${c.new} new lead${c.new === 1 ? '' : 's'}${c.assigned ? (to ? `, all given to ${to}` : `, ${c.assigned} handed out`) : ''}${c.waiting ? `, ${c.waiting} waiting` : ''}${c.existing ? ` · ${c.existing} already leads` : ''}`);
      setPasted(''); setPasteCheck(null);
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const addOne = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!one.phone.trim()) { toast.error('Enter the phone number'); return; }
    if (!one.leadType) { toast.error('Pick WhatsApp lead or Form lead'); return; }
    setSaving(true);
    try {
      const r = await crm.addLead(auth, { ...one, assignTo: oneTo });
      const l = r.lead;
      toast.success(r.status === 'existing'
        ? `Already a lead${l?.ownerName ? `: stays with ${l.ownerName}` : ''}`
        : l?.ownerName ? `Added: given to ${l.ownerName}` : `Added: ${WAIT_LABEL[l?.waiting || ''] || 'waiting'}`);
      setOne({ name: '', phone: '', email: '', course: one.course, source: one.source, notes: '', leadType: one.leadType, mode: '' });
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const readFile = async (f: File) => {
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await f.arrayBuffer(), { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const all = (XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' }) as unknown[][])
        .map(r => r.map(c => String(c ?? '').trim()))
        .filter(r => r.some(Boolean));
      if (!all.length) { toast.error('That file has no rows'); return; }
      // A first row with words and no phone number is the heading
      const hasHeader = all[0].some(c => /[a-z]/i.test(c)) && !all[0].some(c => c.replace(/\D/g, '').length >= 10);
      const header = hasHeader ? all[0] : all[0].map((_, i) => `Column ${String.fromCharCode(65 + i)}`);
      const rows = hasHeader ? all.slice(1) : all;
      let guess = guessColumns(header);
      if (guess.phone === -1) {
        // No heading: the column that looks like phone numbers
        const phoneCol = header.findIndex((_, i) => rows.slice(0, 20).filter(r => (r[i] || '').replace(/\D/g, '').length >= 10).length >= Math.min(3, rows.length));
        guess = { ...guess, phone: phoneCol, name: guess.name === -1 ? header.findIndex((_, i) => i !== phoneCol) : guess.name };
      }
      setFile({ name: f.name, header, rows });
      setCols(guess);
      setCheck(null);
    } catch (err: any) { toast.error(`Could not read that file: ${err.message}`); }
  };

  const mapped = useCallback((): RowIn[] => {
    if (!file || !cols) return [];
    return file.rows.map(r => Object.fromEntries(FIELDS.map(f => [f, cols[f] >= 0 ? r[cols[f]] || '' : ''])) as RowIn);
  }, [file, cols]);

  const runCheck = async () => {
    if (!cols || cols.phone < 0) { toast.error('Pick the column with the phone numbers'); return; }
    setSaving(true);
    try { setCheck(await crm.checkLeads(auth, mapped())); } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const runImport = async () => {
    if (!upType && !(cols && cols.leadType >= 0)) { toast.error('Pick WhatsApp leads or Form leads for this file'); return; }
    setSaving(true);
    try {
      const r = await crm.importLeads(auth, mapped(), upTo, upSource, upType);
      const c = r.counts;
      toast.success(`Saved ${c.new} new lead${c.new === 1 ? '' : 's'}${c.assigned ? `, ${c.assigned} handed out` : ''}${c.waiting ? `, ${c.waiting} waiting` : ''}${c.existing ? ` · ${c.existing} already leads` : ''}`);
      setFile(null); setCols(null); setCheck(null);
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  if (error) return <Empty icon="⚠️" title="Could not open the CRM" sub={error} />;
  if (!me) return <Spinner />;
  return (
    <div className="space-y-5">
      <PageHead title="Add Leads" sub="Paste numbers, add one at a time, or upload an Excel / CSV file. A number the CRM already knows stays with its salesperson." />

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
        <div>
          <p className="font-bold text-gray-800">Paste numbers</p>
          <p className="text-xs text-gray-400 mt-0.5">Only the phone numbers, separated by commas (or one per line). 20–30 at a time is fine.</p>
        </div>
        <label className="sr-only" htmlFor="p-numbers">Phone numbers</label>
        <textarea id="p-numbers" rows={4} value={pasted} onChange={e => { setPasted(e.target.value); setPasteCheck(null); }}
          placeholder="98765 43210, 91234 56789, 99887 76655" className={`${inputCls} font-mono resize-y`} />
        <p className="text-xs font-semibold text-gray-500">{numbers.length} number{numbers.length === 1 ? '' : 's'}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={labelCls} htmlFor="p-to">Who gets them</label><GiveTo id="p-to" people={people} value={pasteTo} onChange={setPasteTo} /></div>
          <div>
            <label className={labelCls} htmlFor="p-source">Source</label>
            <select id="p-source" value={pasteSource} onChange={e => setPasteSource(e.target.value)} className={inputCls}>
              <option value="">—</option>{me.lists.sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><TypePick label="These leads are *" value={pasteType} onPick={setPasteType} /></div>
        </div>
        {!pasteCheck ? (
          <button type="button" onClick={checkPasted} disabled={saving || !numbers.length} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-60" style={purpleBg}>
            {saving ? 'Checking…' : `Check ${numbers.length || ''} number${numbers.length === 1 ? '' : 's'}`}
          </button>
        ) : (
          <div className="space-y-3">
            <CheckChips counts={pasteCheck.counts} what="list" />
            {pasteCheck.rows.some(r => r.check !== 'new') && (
              <ul className="text-xs space-y-1 max-h-48 overflow-y-auto border border-gray-100 rounded-xl p-3">
                {pasteCheck.rows.filter(r => r.check !== 'new').map(r => (
                  <li key={r.i}><span className="font-mono">{fmtPhone(r.phone)}</span> · <span className={`font-bold ${CHECK_LABEL[r.check].cls}`}>{CHECK_LABEL[r.check].text}{r.owner ? `: ${r.owner} has it` : ''}{r.stage ? ` (${r.stage})` : ''}</span></li>
                ))}
              </ul>
            )}
            <button type="button" onClick={savePasted} disabled={saving || (!pasteCheck.counts.new && !pasteCheck.counts.existing)} className={btnGold} style={goldBg}>
              {saving ? 'Saving…' : `Save ${pasteCheck.counts.new} new lead${pasteCheck.counts.new === 1 ? '' : 's'}${pasteTo !== 'turn' ? ` for ${people.find(p => p.id === pasteTo)?.name || ''}` : ''}`}
            </button>
          </div>
        )}
      </div>

      <form onSubmit={addOne} className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
        <p className="font-bold text-gray-800">One lead</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={labelCls} htmlFor="a-phone">Phone *</label><input id="a-phone" value={one.phone} onChange={e => setOne(o => ({ ...o, phone: e.target.value }))} placeholder="98765 43210" className={inputCls} /></div>
          <div><label className={labelCls} htmlFor="a-name">Name</label><input id="a-name" value={one.name} onChange={e => setOne(o => ({ ...o, name: e.target.value }))} className={inputCls} /></div>
          <div><label className={labelCls} htmlFor="a-email">Email</label><input id="a-email" type="email" value={one.email} onChange={e => setOne(o => ({ ...o, email: e.target.value }))} className={inputCls} /></div>
          <div>
            <label className={labelCls} htmlFor="a-course">Course</label>
            <select id="a-course" value={one.course} onChange={e => setOne(o => ({ ...o, course: e.target.value }))} className={inputCls}>
              <option value="">—</option>{me.lists.courses.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="a-source">Source</label>
            <select id="a-source" value={one.source} onChange={e => setOne(o => ({ ...o, source: e.target.value }))} className={inputCls}>
              <option value="">—</option>{me.lists.sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="a-mode">Online / offline</label>
            <select id="a-mode" value={one.mode} onChange={e => setOne(o => ({ ...o, mode: e.target.value }))} className={inputCls}>
              <option value="">Not known yet</option><option value="Online">Online</option><option value="Offline">Offline</option>
            </select>
          </div>
          <div><label className={labelCls} htmlFor="a-to">Who gets it</label><GiveTo id="a-to" people={people} value={oneTo} onChange={setOneTo} /></div>
          <div className="sm:col-span-2"><TypePick label="Lead type *" value={one.leadType} onPick={v => setOne(o => ({ ...o, leadType: v }))} /></div>
          <div className="sm:col-span-2"><label className={labelCls} htmlFor="a-notes">Notes</label><input id="a-notes" value={one.notes} onChange={e => setOne(o => ({ ...o, notes: e.target.value }))} className={inputCls} /></div>
        </div>
        <button type="submit" disabled={saving} className={btnGold} style={goldBg}>{saving ? 'Adding…' : 'Add lead'}</button>
      </form>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div>
          <p className="font-bold text-gray-800">Upload Excel or CSV</p>
          <p className="text-xs text-gray-400 mt-0.5">One lead per row. A heading row (Name, Phone, Email, Course, Source, Notes, Lead type, Online / offline) helps; the phone column is found either way.</p>
        </div>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ''; }} className="text-sm" />
        {file && cols && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600"><b>{file.name}</b> · {file.rows.length} rows</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {FIELDS.map(f => (
                <div key={f}>
                  <label className={labelCls} htmlFor={`col-${f}`}>{FIELD_LABEL[f]}{f === 'phone' ? ' *' : ''}</label>
                  <select id={`col-${f}`} value={cols[f]} onChange={e => { setCols(c => ({ ...c!, [f]: Number(e.target.value) })); setCheck(null); }} className={inputCls}>
                    <option value={-1}>— none —</option>
                    {file.header.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls} htmlFor="u-source">Source for rows without one</label>
                <select id="u-source" value={upSource} onChange={e => setUpSource(e.target.value)} className={inputCls}>
                  <option value="">—</option>{me.lists.sources.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label className={labelCls} htmlFor="u-to">Who gets them</label><GiveTo id="u-to" people={people} value={upTo} onChange={setUpTo} /></div>
              <div className="sm:col-span-2"><TypePick label={cols.leadType >= 0 ? 'Lead type for rows without one' : 'These leads are *'} value={upType} onPick={setUpType} /></div>
            </div>
            <button onClick={runCheck} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-60" style={purpleBg}>{saving && !check ? 'Checking…' : 'Check before saving'}</button>

            {check && (
              <div className="space-y-3">
                <CheckChips counts={check.counts} what="file" />
                <div className="overflow-x-auto border border-gray-100 rounded-xl max-h-80">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0"><tr>{['Row', 'Name', 'Phone', 'Source', 'Type', 'Online / offline', 'Check'].map(h => <th key={h} className="text-left px-3 py-2 font-bold text-gray-500">{h}</th>)}</tr></thead>
                    <tbody>
                      {check.rows.slice(0, 300).map(r => (
                        <tr key={r.i} className="border-t border-gray-100">
                          <td className="px-3 py-1.5 text-gray-400">{r.i + 1}</td>
                          <td className="px-3 py-1.5">{r.name}</td>
                          <td className="px-3 py-1.5 font-mono">{fmtPhone(r.phone)}</td>
                          <td className="px-3 py-1.5">{r.source || upSource}</td>
                          <td className="px-3 py-1.5">{r.leadType || upType || '—'}</td>
                          <td className="px-3 py-1.5">{r.mode || '—'}</td>
                          <td className={`px-3 py-1.5 font-bold ${CHECK_LABEL[r.check].cls}`}>{CHECK_LABEL[r.check].text}{r.owner ? `: ${r.owner} has it` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {check.rows.length > 300 && <p className="text-xs text-gray-400">Showing the first 300 rows.</p>}
                <button onClick={runImport} disabled={saving || (!check.counts.new && !check.counts.existing)} className={btnGold} style={goldBg}>
                  {saving ? 'Saving…' : `Save ${check.counts.new} new lead${check.counts.new === 1 ? '' : 's'}`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── ALL LEADS ────────────────────────────────────────────────

export const CrmAllLeads: React.FC<{ auth: CrmAuth; embedded?: boolean }> = ({ auth, embedded }) => {
  const { me, error } = useCrmMe(auth);
  const [f, setF] = useState({ stage: 'open', ownerId: '', source: '', leadType: '', from: '', to: '' });
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.allLeads>> | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [moveTo, setMoveTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [junking, setJunking] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setQuery(q.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [q]);
  const load = useCallback(async () => {
    try {
      setData(await crm.allLeads(auth, { ...f, q: query, page, limit: 50 }));
    } catch (e: any) { toast.error(e.message); }
  }, [auth, f, query, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSel(new Set()); }, [f, query, page]);

  const move = async () => {
    if (!moveTo || !sel.size) return;
    if (moveTo === 'junk') { setJunking(true); return; }
    setBusy(true);
    try {
      const r = await crm.moveLeads(auth, Array.from(sel), moveTo);
      toast.success(`${r.moved} moved${r.waiting ? `, ${r.waiting} waiting (nobody free)` : ''}${r.skipped ? `, ${r.skipped} skipped (closed or already theirs)` : ''}`);
      setSel(new Set()); setMoveTo('');
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const setFilter = (k: keyof typeof f, v: string) => { setF(x => ({ ...x, [k]: v })); setPage(1); };
  if (error) return <Empty icon="⚠️" title="Could not open the CRM" sub={error} />;
  const people = me?.people || [];
  const leads = data?.leads || [];
  const allOn = leads.length > 0 && leads.every(l => sel.has(l.id));
  return (
    <div className="space-y-4">
      <Head embedded={embedded} title="All Leads" onRefresh={load}
        sub={`${data?.total ?? '…'} leads${data?.waitingCount ? ` · ${data.waitingCount} waiting for the next working morning or someone free` : ''}. Tick leads to move them together.`} />

      {/* WhatsApp leads and form leads, each on its own tab */}
      <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-white border border-gray-100 w-fit" role="tablist" aria-label="Lead type">
        {[['', 'All leads'], ['WhatsApp', 'WhatsApp leads'], ['Form', 'Form leads'], ['none', 'Type not set']].map(([k, label]) => {
          const tc = data?.typeCounts || {};
          const n = k === '' ? Object.values(tc).reduce((a, b) => a + b, 0) : tc[k] || 0;
          if (k === 'none' && !n && f.leadType !== 'none') return null;
          return (
            <button key={k} role="tab" aria-selected={f.leadType === k} onClick={() => setFilter('leadType', k)} className="px-3 py-1.5 rounded-lg text-xs font-bold"
              style={f.leadType === k ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>
              {label} <span className="opacity-60">{n}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, number, #…" className={`${inputCls} col-span-2`} />
        <select value={f.stage} onChange={e => setFilter('stage', e.target.value)} className={inputCls} aria-label="Stage">
          <option value="open">Open</option><option value="all">All stages</option><option value="closed">Closed</option>
          {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={f.ownerId} onChange={e => setFilter('ownerId', e.target.value)} className={inputCls} aria-label="Salesperson">
          <option value="">Anyone</option><option value="none">No one (waiting, or Junk)</option>
          {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={f.source} onChange={e => setFilter('source', e.target.value)} className={inputCls} aria-label="Source">
          <option value="">Any source</option>{(me?.lists.sources || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex gap-2 col-span-2 lg:col-span-1">
          <input type="date" value={f.from} onChange={e => setFilter('from', e.target.value)} className={inputCls} aria-label="Added from" title="Added from" />
          <input type="date" value={f.to} onChange={e => setFilter('to', e.target.value)} className={inputCls} aria-label="Added until" title="Added until" />
        </div>
      </div>

      {sel.size > 0 && (
        <div className="sticky top-2 z-20 bg-white rounded-2xl border-2 border-purple-200 shadow-lg p-3 flex flex-wrap items-center gap-2">
          <p className="text-sm font-bold text-gray-800 mr-2">{sel.size} selected</p>
          <select value={moveTo} onChange={e => setMoveTo(e.target.value)} className={`${inputCls} max-w-xs`}>
            <option value="">Move to…</option><option value="turn">Back into the turn</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
            <option value="junk">🚮 Junk Leads…</option>
          </select>
          <button onClick={move} disabled={!moveTo || busy} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={purpleBg}>Move</button>
          <button onClick={() => setSel(new Set())} className="text-xs font-semibold text-gray-500 px-2">Clear</button>
        </div>
      )}

      {!data || !me ? <Spinner /> : leads.length === 0 ? (
        <Empty icon="📭" title="No leads match" />
      ) : (
        <>
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 px-1 cursor-pointer w-fit">
            <input type="checkbox" checked={allOn} onChange={e => setSel(e.target.checked ? new Set(leads.map(l => l.id)) : new Set())} className="w-4 h-4 accent-purple-700" />
            Select all on this page
          </label>
          <div className="space-y-2">
            {leads.map(l => (
              <LeadRow key={l.id} lead={l} showOwner maxOwners={data.maxOwners} onOpen={() => setOpen(l.id)}
                selected={sel.has(l.id)} onSelect={on => setSel(s => { const n = new Set(s); if (on) n.add(l.id); else n.delete(l.id); return n; })} />
            ))}
          </div>
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded-lg border text-xs font-bold disabled:opacity-40">← Previous</button>
              <span className="text-xs text-gray-500">Page {data.page} of {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded-lg border text-xs font-bold disabled:opacity-40">Next →</button>
            </div>
          )}
        </>
      )}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
      {junking && me && (
        <JunkLeadModal auth={auth} me={me} leadIds={Array.from(sel)} title={`Move ${sel.size} lead${sel.size === 1 ? '' : 's'} to Junk Leads?`}
          sub="Converted leads, P2P leads and leads with a coordinator are skipped" onClose={() => setJunking(false)}
          onDone={(moved, skipped) => {
            setJunking(false);
            toast.success(`${moved} moved to Junk Leads${skipped ? `, ${skipped} skipped (converted, in P2P, with a coordinator, or in Junk already)` : ''}`);
            setSel(new Set()); setMoveTo('');
            load();
          }} />
      )}
    </div>
  );
};

// ── TEAM TODAY ───────────────────────────────────────────────

export const CrmTeamToday: React.FC<{ auth: CrmAuth; embedded?: boolean; onHolidays?: () => void }> = ({ auth, embedded, onHolidays }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.teamToday>> | null>(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    try { setData(await crm.teamToday(auth)); } catch (e: any) { toast.error(e.message); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);
  const pause = async (t: TeamRow) => {
    setBusy(t.id);
    try {
      await crm.setPaused(auth, t.id, !t.paused);
      toast.success(`${t.name} ${t.paused ? 'gets leads again' : 'is paused: no new leads'}`);
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(''); }
  };
  // A Lead Manager / Sales Admin taking new leads too, or not (a Lead
  // Manager gets the leads the salespeople couldn't reach either way)
  const takes = async (t: TeamRow) => {
    setBusy(t.id);
    try {
      await crm.setTakesLeads(auth, !t.takesLeads, t.id);
      toast.success(`${t.name} ${t.takesLeads ? 'no longer gets new leads' : 'gets new leads too, in turn'}`);
      forgetCrmMe();
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(''); }
  };
  if (!data) return <Spinner label="Loading the team…" />;
  const cols: [keyof TeamRow, string][] = [['givenToday', 'New today'], ['movedToday', 'Moved to them'], ['calledToday', 'Calls'], ['talkedToday', 'Talked'], ['toCall', 'Not called yet'], ['pollsDue', 'Polls due'], ['interested', 'Interested'], ['open', 'Open leads'], ['lateFollowUps', 'Late']];
  const red: (keyof TeamRow)[] = ['lateFollowUps', 'pollsDue'];
  return (
    <div className="space-y-4">
      <Head embedded={embedded} title="Team Today" onRefresh={load}
        sub={`${data.working ? 'A working day' : 'Not a working day'} · ${data.waiting} lead${data.waiting === 1 ? '' : 's'} waiting`} />
      <HolidayBanner note={data.holiday} manager onOpen={onHolidays} />
      <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-gray-50"><tr>
            <th className="text-left px-4 py-3 text-xs font-bold text-gray-500">Person</th>
            <th className="text-left px-3 py-3 text-xs font-bold text-gray-500">Today</th>
            {cols.map(([, label]) => <th key={label} className="text-right px-3 py-3 text-xs font-bold text-gray-500 whitespace-nowrap">{label}</th>)}
            <th className="px-3 py-3" />
          </tr></thead>
          <tbody>
            {data.team.map(t => {
              // A Lead Manager calls the leads the salespeople couldn't reach, switch on or off
              const calls = t.inTurn || t.role === 'Lead Manager';
              return (
              <tr key={t.id} className="border-t border-gray-100">
                <td className="px-4 py-3"><p className="font-bold text-gray-800">{t.name}</p><p className="text-[11px] text-gray-400">{t.id} · {t.role}</p></td>
                <td className="px-3 py-3 space-y-1">
                  <span className={`inline-block text-[11px] font-bold px-2 py-1 rounded-full ${!calls ? 'bg-gray-100 text-gray-500' : t.onLeave ? 'bg-sky-100 text-sky-700' : t.paused ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'}`}>
                    {!calls ? 'Not taking leads' : t.onLeave ? 'On leave' : t.paused ? 'Paused' : t.awayNow ? 'Away (half day)' : t.inTurn ? 'Working' : 'Leads not picked only'}
                  </span>
                  {calls && <div><ShiftChip shift={t.shift} onNow={t.onShiftNow} /></div>}
                  {t.halfDay && <p className="text-[10px] text-sky-700 font-semibold whitespace-nowrap">Half day {fmtHm(t.halfDay.from)} – {fmtHm(t.halfDay.to)}</p>}
                  {/* Sales attendance: a Salesperson gets new leads only after Start work */}
                  {t.work && t.role === 'Salesperson' && !t.onLeave && (
                    <p className={`text-[10px] font-semibold whitespace-nowrap ${!t.work.startAt ? 'text-red-600' : t.work.endAt ? 'text-gray-500' : t.work.lateMinutes > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                      {!t.work.startAt ? 'No Start work yet' : t.work.endAt ? `Ended ${fmtWhen(t.work.endAt).replace('Today ', '')}`
                        : `Started ${fmtWhen(t.work.startAt).replace('Today ', '')}${t.work.lateMinutes > 0 ? ` · ${t.work.lateMinutes} min late` : ''}`}
                    </p>
                  )}
                </td>
                {cols.map(([k, label]) => (
                  <td key={label} className={`px-3 py-3 text-right font-semibold tabular-nums ${red.includes(k) && (t[k] as number) > 0 ? 'text-red-600' : 'text-gray-700'}`}>{t[k] as number}</td>
                ))}
                <td className="px-3 py-3 text-right space-x-2 whitespace-nowrap">
                  {t.role !== 'Salesperson' && (
                    <button onClick={() => takes(t)} disabled={busy === t.id} className="text-xs font-bold px-3 py-1.5 rounded-lg border-2 border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50">
                      {t.takesLeads ? 'Stop new leads' : 'New leads too'}
                    </button>
                  )}
                  {calls && (
                    <button onClick={() => pause(t)} disabled={busy === t.id} className="text-xs font-bold px-3 py-1.5 rounded-lg border-2 border-gray-200 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap">
                      {t.paused ? 'Resume' : 'Pause'}
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 px-1">New leads go to whoever is on shift now. A paused person gets no new leads and no moved leads, and keeps the ones they have. Approved leave pauses them by itself: a full day for the day, a half day for its hours. A Lead Manager gets the leads the salespeople couldn't reach (after 3 tries each), and new leads only with "New leads too"; a Sales Admin gets leads only with it.</p>
    </div>
  );
};

// ── RULES ────────────────────────────────────────────────────

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const LIST_LABELS: [keyof CrmLists, string][] = [['coldReasons', 'Cold Lead reasons'], ['notInterestedReasons', 'Not Interested reasons'], ['sources', 'Lead sources'], ['courses', 'Courses']];

export const CrmRulesPage: React.FC<{ auth: CrmAuth; embedded?: boolean; onNavigate?: (page: string) => void }> = ({ auth, embedded, onNavigate }) => {
  const [rules, setRules] = useState<CrmRules | null>(null);
  const [lists, setLists] = useState<Record<keyof CrmLists, string> | null>(null);
  const [people, setPeople] = useState<CrmPerson[]>([]);
  const [meta, setMeta] = useState('');
  const [saving, setSaving] = useState(false);
  const fill = (r: Awaited<ReturnType<typeof crm.getSettings>>) => {
    setRules(r.rules);
    setLists(Object.fromEntries(LIST_LABELS.map(([k]) => [k, r.lists[k].join('\n')])) as Record<keyof CrmLists, string>);
    setPeople(r.people);
    setMeta(r.updatedBy ? `Last changed by ${r.updatedBy}${r.updatedAt ? `, ${fmtWhen(r.updatedAt)}` : ''}` : '');
  };
  useEffect(() => {
    crm.getSettings(auth).then(fill).catch(e => toast.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);
  if (!rules || !lists) return <Spinner label="Loading the rules…" />;

  const set = <K extends keyof CrmRules>(k: K, v: CrmRules[K]) => setRules(r => ({ ...r!, [k]: v }));
  const num = (k: keyof CrmRules) => (e: React.ChangeEvent<HTMLInputElement>) => set(k, Number(e.target.value) as never);
  const nameOf = (id: string) => people.find(p => p.id === id)?.name || id;
  const moveOrder = (i: number, d: -1 | 1) => {
    const o = [...rules.order];
    const j = i + d;
    if (j < 0 || j >= o.length) return;
    [o[i], o[j]] = [o[j], o[i]];
    set('order', o);
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await crm.saveSettings(auth, rules, Object.fromEntries(LIST_LABELS.map(([k]) => [k, lists[k].split('\n').map(s => s.trim()).filter(Boolean)])));
      fill(r);
      forgetCrmMe();
      toast.success('Rules saved');
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const numField = (k: keyof CrmRules, label: string, hint: string, step = 1) => (
    <div>
      <label className={labelCls} htmlFor={`r-${k}`}>{label}</label>
      <input id={`r-${k}`} type="number" step={step} value={rules[k] as number} onChange={num(k)} className={inputCls} />
      <p className="text-[11px] text-gray-400 mt-1">{hint}</p>
    </div>
  );

  return (
    <div className="space-y-5">
      <Head embedded={embedded} title="Rules" sub={meta || 'How leads are handed out and moved'} />
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-5">
        <div>
          <p className="font-bold text-gray-800">Working time</p>
          <p className="text-xs text-gray-400 mt-0.5">The two shifts sit inside it: set their times on Team → Shifts.</p>
        </div>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div><label className={labelCls} htmlFor="r-start">Starts</label><input id="r-start" type="time" value={rules.workStart} onChange={e => set('workStart', e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls} htmlFor="r-end">Ends</label><input id="r-end" type="time" value={rules.workEnd} onChange={e => set('workEnd', e.target.value)} className={inputCls} /></div>
        </div>
        <div>
          <p className={labelCls}>Working days</p>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => {
              const on = rules.workDays.includes(i);
              return (
                <button key={d} type="button" onClick={() => set('workDays', on ? rules.workDays.filter(x => x !== i) : [...rules.workDays, i].sort())}
                  className="w-14 py-2 rounded-xl text-xs font-bold border-2" style={on ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' } : { color: '#6b7280', borderColor: '#e5e7eb' }}>{d}</button>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Holidays are skipped as well: the institute's, and the office's own (set them on Team &amp; Shifts → Holidays).
            {onNavigate && <button type="button" onClick={() => onNavigate('team:holidays')} className="ml-1 font-bold text-purple-700 hover:underline">Holidays →</button>}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <p className="font-bold text-gray-800">Moving leads that are not taken</p>
        <p className="text-xs text-gray-500">Not taken means never called, or only Not Picked (or Call Later before any brochure). Each salesperson gets 3 tries (Not Picked calls), then each Lead Manager 3 tries, then the lead goes to Junk Leads. The lead moves on when their shift ends (to the shift still on, or the morning shift the next working day) or after their 3 tries, and can come back to a salesperson with tries left; the Lead Managers get it once no salesperson working that day has any. Once a brochure goes, the lead stays with them.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {numField('daysPerOwner', 'Days with one person', 'Working days before it moves on')}
          {numField('maxOwners', 'Salespeople it passes through', 'Then the Lead Managers, then Junk Leads')}
          {numField('dailyCap', 'New leads per person per day', '0 = no limit')}
        </div>
        <div>
          <p className={labelCls}>Order: salespeople, then Lead Managers</p>
          <ol className="space-y-1.5 max-w-md">
            {rules.order.map((id, i) => (
              <li key={id} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <span className="flex-1 text-sm font-semibold text-gray-800">{nameOf(id)} <span className="text-gray-400 font-normal">{id}</span></span>
                <button type="button" onClick={() => moveOrder(i, -1)} disabled={i === 0} className="px-2 py-1 rounded-lg border text-xs disabled:opacity-30" aria-label="Move up">↑</button>
                <button type="button" onClick={() => moveOrder(i, 1)} disabled={i === rules.order.length - 1} className="px-2 py-1 rounded-lg border text-xs disabled:opacity-30" aria-label="Move down">↓</button>
              </li>
            ))}
          </ol>
          <p className="text-[11px] text-gray-400 mt-1">A lead nobody picks up goes down this list: the salespeople (and a Sales Admin with "New leads too") always come first, then the Lead Managers in their order here, then Junk Leads.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <p className="font-bold text-gray-800">Reminders</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {numField('retryHours', 'Not Picked: try again after (hours)', 'Same day', 0.5)}
          {numField('pollHours', 'Poll Sent: update its status within (hours)', 'The reminder comes then, in their shift', 0.5)}
          <div>
            <label className={labelCls} htmlFor="r-nc">"Not called yet" reminder</label>
            <input id="r-nc" type="time" value={rules.notCalledAt} onChange={e => set('notCalledAt', e.target.value)} className={inputCls} />
            <p className="text-[11px] text-gray-400 mt-1">To anyone with today's leads uncalled</p>
          </div>
          {numField('remindBefore', 'Call Later reminder (minutes before)', 'Visits: an hour before')}
          {numField('lateHours', 'Follow-up late: tell the Lead Manager after (hours)', 'When nobody called at the time', 0.5)}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <p className="font-bold text-gray-800">Lists in the forms</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {LIST_LABELS.map(([k, label]) => (
            <div key={k}>
              <label className={labelCls} htmlFor={`l-${k}`}>{label} <span className="normal-case font-normal">(one per line)</span></label>
              <textarea id={`l-${k}`} rows={6} value={lists[k]} onChange={e => setLists(l => ({ ...l!, [k]: e.target.value }))} className={`${inputCls} resize-y`} />
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="px-6 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={purpleBg}>{saving ? 'Saving…' : 'Save rules'}</button>
    </div>
  );
};

// ── MOVE HISTORY ─────────────────────────────────────────────

export const CrmMoveHistory: React.FC<{ auth: CrmAuth; embedded?: boolean }> = ({ auth, embedded }) => {
  const { me } = useCrmMe(auth);
  const [date, setDate] = useState('');
  const [person, setPerson] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ moves: CrmActivity[]; total: number; page: number; pages: number } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setData(await crm.moveHistory(auth, { date, salesPersonId: person, page })); } catch (e: any) { toast.error(e.message); }
  }, [auth, date, person, page]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4">
      <Head embedded={embedded} title="Move History" sub="Every hand-out and move, automatic or by hand, and why" />
      <div className="flex flex-wrap gap-3">
        <input type="date" value={date} onChange={e => { setDate(e.target.value); setPage(1); }} className={`${inputCls} max-w-[180px]`} aria-label="Day" />
        <select value={person} onChange={e => { setPerson(e.target.value); setPage(1); }} className={`${inputCls} max-w-[220px]`} aria-label="Salesperson">
          <option value="">Everyone</option>{(me?.people || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      {!data ? <Spinner /> : data.moves.length === 0 ? <Empty icon="🔀" title="No moves" /> : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {data.moves.map(m => (
            <button key={m.id} onClick={() => setOpen(m.leadId)} className="w-full text-left px-4 py-3 hover:bg-purple-50/40 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
              <span className="text-[11px] text-gray-400 sm:w-36 flex-shrink-0">{fmtWhen(m.at)}</span>
              <span className="text-sm text-gray-800 flex-1 min-w-0">
                <b>#{m.leadNo} {m.leadName || 'No name'}</b>{' · '}
                {m.type === 'waiting' ? `${m.fromName ? `${m.fromName} → ` : ''}Waiting list` : `${m.fromName ? `${m.fromName} → ` : ''}${m.toName}`}
                {m.byName && m.byName !== 'System' && <span className="text-gray-400"> · by {m.byName}</span>}
              </span>
              <span className="text-xs text-gray-500 sm:max-w-xs">{m.reason}</span>
            </button>
          ))}
        </div>
      )}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded-lg border text-xs font-bold disabled:opacity-40">← Newer</button>
          <span className="text-xs text-gray-500">Page {data.page} of {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded-lg border text-xs font-bold disabled:opacity-40">Older →</button>
        </div>
      )}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};
